import { Ability, AbilityBuilder } from '@casl/ability';
import User from 'Js/api/v1/model/User';
import Thread from 'Js/api/v1/forum2/Thread';
import Topic from 'Js/api/v1/forum2/Topic';
import Reply from 'Js/api/v1/forum2/Reply';

/**
 * @see https://stalniy.github.io/casl/
 * @see https://github.com/crcn/sift.js
 * @see https://docs.mongodb.com/manual/reference/operator/query/#query-selectors
 * @param {User} user - текущий пользователь
 * @param {Set} roles - права текущего пользователя
 * @param {boolean} isEmulatingUser - текущий пользователь эмулируется администратором
 * @param {Map} featureFlags - фичи региональной площадки (см. FeatureFlags.cs)
 *
 * Настройка прав должна выполняться по принципам:
 * - сначала более общие правила, затем более специфичные,
 *   это позволит избежать некорректного переопределения прав;
 * - выдаём права, а не забираем, меньше шансов на ошибку
 */
export default function buildAbilities(
    user,
    roles,
    isEmulatingUser
    /* , featureFlags */
) {
    const { rules, can: allow, cannot: forbid } = new AbilityBuilder(Ability);

    const { modelName: userModelName } = User;
    const { modelName: threadModelName } = Thread;
    const { modelName: topicModelName } = Topic;
    const { modelName: replyModelName } = Reply;

    // Полноправный администратор может делать всё,
    // и на этом настройка заканчивается
    if (roles.has('FullAccess')) {
        allow('manage', 'all');
        return new Ability(rules);
    }

    // Привилегия совершать вход под другими пользователями
    // и выполнять под ними любые действия в соответствии с их ролями
    if (roles.has('FeatureLoginAs')) {
        allow('manage', userModelName);
    }

    // == Галерея запчастей пользователя ==
    // Привилегия доступа для удаление фотографий запчастей с галереи пользователя
    if (
        roles.has('QA') ||
        roles.has('Developer') ||
        roles.has('Support') ||
        roles.has('FeatureSEO')
    ) {
        allow('delete', 'PartGalleryItem');
    }

    // Привилегия доступа к SEO-параметрам и расширенным описаниям страниц
    // (дополнительные данные обычно выводятся списками в нижней части страницы)
    // Класса SEO не существует, это собирательное название разного доп. функционала
    if (roles.has('FeatureSEO')) {
        allow('manage', 'SEO');
        allow('edit', 'MakerDescription');
    }

    // Пользователь, выполнивший вход под другим пользователем (функция "Войти как")
    // не может закрывать обращения в техподдержку, остальные могут
    if (!isEmulatingUser) {
        allow('update', 'Dialogue', ['isClosed']);
    }

    // == ФОРУМ ==
    // Пользователь по умолчанию не видит удалённые сообщения
    allow('read', [topicModelName, replyModelName], { isDeleted: false });

    // Покупатель, или продавец
    if (roles.has('RegisteredUser')) {
        // может добавлять записи в открытые разделы и темы
        allow('extend', [threadModelName, topicModelName], {
            isSealed: { $eq: false }
        });

        // может подписываться на изменения в открытых темах
        // (технически нет ограничений на подписку на закрытые темы, но в этом мало смысла)
        allow('update', topicModelName, ['isSubscribed'], {
            isSealed: { $eq: false }
        });

        // может изменять свои темы и ответы
        allow(
            'update',
            [topicModelName, replyModelName],
            ['name', 'body', 'files'],
            {
                'author.id': { $eq: user.id }
            }
        );

        // не может изменять значения "особых" полей в теме
        forbid('update', topicModelName, [
            'bodyHtml',
            'publicationDate',
            'isPinned',
            'isSealed'
        ]);

        // может удалять свои темы и ответы, пока на них нет ответов
        allow('delete', [topicModelName, replyModelName], {
            'author.id': { $eq: user.id },
            childrenCount: { $eq: 0 }
        });
    }

    // Копирайтер (обычно пишет новости на форуме)
    if (roles.has('Copywriting')) {
        // может добавлять темы в закрытые разделы (Новости, Видеоблог, и т. п.)
        allow('extend', [threadModelName], {
            isSealed: { $eq: true }
        });

        // совершает любые действия с темами
        allow('manage', [topicModelName]);

        // совершает любые действия со своими ответами
        allow('manage', [replyModelName], {
            'author.id': { $eq: user.id }
        });
    }

    // Модератор форума
    if (roles.has('FeatureForum')) {
        // может добавлять темы в закрытые разделы (Новости, Видеоблог, и т. п.)
        allow('extend', [threadModelName], {
            isSealed: { $eq: true }
        });

        // видит все записи, даже удалённые
        allow('read', 'all');

        // совершает любые действия с темами и ответами
        allow('manage', [topicModelName, replyModelName]);
    }

    return new Ability(rules);
}
